import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, Landmark, Link2, Receipt, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "../../../lib/format";
import { STATUS_CLS, newKey, payErr } from "../../../lib/payments";
import { useApi } from "../hooks";
import { RfCard } from "../RfCard";
import { Panel, btnGhost, btnPrimary, chip, input } from "../../../components/common/Page";
import { Checkbox } from "../../../components/ui/checkbox";
import { SkeletonBlock } from "../../../components/common/EmptyState";

const ICONS = { card: CreditCard, iris: Landmark, softone_link: Link2 };
const SCOPES = ["balance", "overdue", "invoices", "custom"];

const ModeTag = ({ mode }) => {
  const { t } = useI18n();
  if (mode === "live") return null;
  return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">{t(mode === "test" ? "x.pay.test" : "x.pay.mock")}</span>;
};

const Invoices = ({ invoices, sel, setSel }) => {
  const { t } = useI18n();
  const toggle = (d) => setSel((s) => (s.includes(d) ? s.filter((x) => x !== d) : [...s, d]));
  return (
    <ul className="mt-3 max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-100" data-testid="pay-invoice-list">
      {invoices.map((i) => (
        <li key={i.document}>
          <label className="flex min-h-12 cursor-pointer items-center gap-3 px-3 py-2 text-sm">
            <Checkbox checked={sel.includes(i.document)} onCheckedChange={() => toggle(i.document)} data-testid={`pay-invoice-${i.document}`} />
            <span className="min-w-0 flex-1"><span className="font-medium text-navy">{i.document}</span>
              <span className={`block text-xs ${i.overdue ? "text-rose-600" : "text-slate-500"}`}>{t("x.pay.due")} {fmtDate(i.due_date)}</span></span>
            <span className="tabular-nums font-semibold text-navy">{fmtMoney(i.open_amount)}</span>
          </label>
        </li>
      ))}
    </ul>
  );
};

const History = () => {
  const { t } = useI18n();
  const r = useApi("myPayments", "/payments/mine", undefined, { staleTime: 0 });
  return (
    <Panel title={t("x.pay.history")} testId="pay-history">
      {r.isLoading ? <SkeletonBlock className="h-20" /> : !r.data?.length ? <p className="text-sm text-slate-500">{t("x.pay.none")}</p> : (
        <ul className="divide-y divide-slate-100">
          {r.data.map((p) => (
            <li key={p.id}><Link to={`/app/pay/status/${p.id}`} className="flex items-center gap-3 py-3 text-sm" data-testid={`pay-history-${p.id}`}>
              <Receipt className="h-4 w-4 text-slate-400" />
              <span className="min-w-0 flex-1"><span className="font-medium text-navy">{p.receipt_no || t(`x.pay.${p.method}`)}</span><span className="block text-xs text-slate-500">{fmtDateTime(p.created_at)}</span></span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CLS[p.status]}`}>{t(`x.pay.status.${p.status}`)}</span>
              <span className="w-24 text-right font-semibold tabular-nums text-navy">{fmtMoney(p.total)}</span>
            </Link></li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

const amountOf = (s, scope, sel, custom) => {
  if (scope === "balance") return s.balance;
  if (scope === "overdue") return s.overdue;
  if (scope === "invoices") return s.invoices.filter((i) => sel.includes(i.document)).reduce((a, i) => a + i.open_amount, 0);
  return Number(String(custom).replace(",", ".")) || 0;
};

export default function Pay() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const s = useApi("paySummary", "/payments/summary", undefined, { staleTime: 0 });
  const { user } = useAuth();
  const fin = user.permissions.includes("finance:read");
  const scopes = fin ? SCOPES : ["custom"];
  const [scope, setScope] = useState(fin ? params.get("scope") || "balance" : "custom");
  const [sel, setSel] = useState([]);
  const [custom, setCustom] = useState("");
  const [method, setMethod] = useState("card");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const idem = useMemo(newKey, [scope, sel, custom, method]);
  useEffect(() => {
    const tok = params.get("link");
    if (!tok) return;
    api.get(`/payments/links/${tok}`).then(({ data }) => {
      setLinkToken(tok); setScope(data.scope); setSel(data.documents); setCustom(String(data.amount).replace(".", ","));
    }).catch((e) => toast.error(payErr(t, e)));
  }, [params, t]);
  if (s.isLoading) return <SkeletonBlock className="h-64" />;
  if (!s.data?.enabled) return <p className="rounded-2xl bg-white p-6 text-sm text-slate-500" data-testid="pay-disabled">{t(s.data?.reason === "admin_only" ? "x.pay.adminOnly" : "x.pay.err.payments_disabled")}</p>;
  const d = s.data;
  const amount = Math.round(amountOf(d, scope, sel, custom) * 100) / 100;
  const m = d.methods[method];
  const fee = Math.round(amount * m.fee_pct) / 100;
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/intents", { idem_key: idem, method, scope, documents: scope === "invoices" ? sel : [], amount: scope === "custom" ? amount : null, link_token: linkToken });
      if (data.redirect_url.startsWith("http")) window.location.href = data.redirect_url; else nav(data.redirect_url);
    } catch (e) { toast.error(payErr(t, e)); setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="pay-page">
      <div><h1 className="text-2xl font-bold text-navy sm:text-3xl">{t("x.pay.title")}</h1><p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><ShieldCheck className="h-4 w-4 text-brand" />{t("x.pay.sub")}</p></div>
      {linkToken && <p className="rounded-xl bg-sky-50 px-4 py-2 text-sm font-medium text-navy" data-testid="pay-from-link">{t("x.pay.fromLink")}</p>}
      {Object.values(d.methods).some((x) => x.mode !== "live") && <p className="rounded-xl bg-amber-50 px-4 py-2 text-xs font-medium text-amber-800" data-testid="pay-test-note">{t("x.pay.testNote")}</p>}
      {!confirm ? <>
        <RfCard compact />
        <Panel title={t("x.pay.what")}>
          <div className="flex flex-wrap gap-2">
            {scopes.map((k) => <button key={k} type="button" className={chip(scope === k)} onClick={() => setScope(k)} data-testid={`pay-scope-${k}`}>{t(`x.pay.${k}`)}{k === "balance" ? ` · ${fmtMoney(d.balance)}` : k === "overdue" ? ` · ${fmtMoney(d.overdue)}` : ""}</button>)}
          </div>
          {scope === "invoices" && <Invoices invoices={d.invoices} sel={sel} setSel={setSel} />}
          {scope === "custom" && <input inputMode="decimal" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="0,00" className={`${input} mt-3 max-w-xs text-lg font-semibold`} data-testid="pay-custom-amount" />}
        </Panel>
        <Panel title={t("x.pay.method")}>
          <div className="grid gap-2 sm:grid-cols-3">
            {Object.entries(d.methods).filter(([, x]) => x.available).map(([k, x]) => {
              const Icon = ICONS[k];
              return (
                <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`pay-method-${k}`}
                  className={`rounded-2xl border-2 p-4 text-left transition-colors ${method === k ? "border-navy bg-navy/5" : "border-slate-100 hover:border-slate-300"}`}>
                  <span className="flex items-center gap-2 font-semibold text-navy"><Icon className="h-5 w-5" />{t(`x.pay.${k}`)}<ModeTag mode={x.mode} /></span>
                  <span className="mt-1 block text-xs text-slate-500">{t(`x.pay.${k}Sub`)}</span>
                  <span className="mt-1 block text-xs font-medium text-slate-600">{x.fee_pct ? `${t("x.pay.fee")} ${String(x.fee_pct).replace(".", ",")}%` : t("x.pay.noFee")}</span>
                </button>
              );
            })}
          </div>
        </Panel>
        <div className="flex items-center justify-between gap-3 rounded-2xl bg-navy p-4 text-white">
          <div><p className="text-xs text-white/70">{t("x.pay.total")}</p><p className="text-2xl font-bold tabular-nums" data-testid="pay-total">{fmtMoney(amount + fee)}</p></div>
          <button type="button" disabled={amount <= 0} onClick={() => setConfirm(true)} className="inline-flex h-12 items-center rounded-full bg-brand px-6 font-semibold text-navy disabled:opacity-50" data-testid="pay-continue">{t("x.pay.continue")}</button>
        </div>
      </> : (
        <Panel title={t("x.pay.confirmTitle")} testId="pay-confirm">
          <dl className="grid gap-2 text-sm">
            {[[t("x.pay.what"), t(`x.pay.${scope}`)], [t("x.pay.method"), t(`x.pay.${method}`)], [t("x.pay.amount"), fmtMoney(amount)], [t("x.pay.fee"), fee ? fmtMoney(fee) : t("x.pay.noFee")]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3"><dt className="text-slate-500">{k}</dt><dd className="font-medium text-navy">{v}</dd></div>
            ))}
            {scope === "invoices" && <div className="text-xs text-slate-500">{sel.join(", ")}</div>}
            <div className="flex justify-between border-t border-slate-100 pt-2 text-base"><dt className="font-semibold text-navy">{t("x.pay.total")}</dt><dd className="font-bold tabular-nums text-navy" data-testid="pay-confirm-total">{fmtMoney(amount + fee)}</dd></div>
          </dl>
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={() => setConfirm(false)} className={`${btnGhost} flex-1`} data-testid="pay-back">{t("x.pay.back")}</button>
            <button type="button" disabled={busy} onClick={pay} className={`${btnPrimary} flex-1`} data-testid="pay-confirm-button">{t("x.pay.confirm")}</button>
          </div>
        </Panel>
      )}
      <History />
    </div>
  );
}
